import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ArrowRight, Mail, Lock } from 'lucide-react';
import { setStoredScamSmartEmail } from '../components/ScamSmartGuard';
import { API_URL } from '../config';
import { trackPurchase } from '../utils/metaPixel';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const ScamSmartThankYou = () => {
  const [searchParams] = useSearchParams();
  const [confetti, setConfetti] = useState(true);
  const amount = searchParams.get('amount') || '14.99';

  useEffect(() => {
    const t = setTimeout(() => setConfetti(false), 3500);
    return () => clearTimeout(t);
  }, []);

  // Meta Pixel — fire Purchase ONLY after verifying payment.
  // For Stripe sessions: verify via /api/scamsmart/session-status (only fires if `paid`).
  // For early-access bypass redemptions (?bypass=1): fire with $0 (legitimate access grant).
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isBypass = searchParams.get('bypass') === '1';

    if (isBypass) {
      // Real access grant via early-access code — count as a $0 conversion
      trackPurchase({
        value: 0,
        order_id: `bypass_${searchParams.get('email') || 'unknown'}`,
        content_id: 'scamsmart-access',
        content_name: 'ScamSmart — Family Scam Defence',
      });
      return;
    }

    if (!sessionId) return; // No session to verify → don't fire Purchase

    fetch(`${API}/scamsmart/session-status?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.paid) {
          console.warn('[ScamSmart] Stripe session not paid — skipping Meta Purchase event', data);
          return;
        }
        const valueDollars = (data.amount_cents || 0) / 100;
        // Deterministic event_id — must match server CAPI event_id sent from
        // the Stripe webhook (`purchase_{stripe_session_id}`) so Meta deduplicates.
        trackPurchase({
          value: valueDollars,
          order_id: sessionId,
          content_id: 'scamsmart-access',
          content_name: 'ScamSmart — Family Scam Defence',
          event_id: `purchase_${sessionId}`,
        });
      })
      .catch((err) => console.warn('[ScamSmart] session-status check failed', err));
  }, []);

  // Capture buyer email into localStorage so the paid-area access guard can use it.
  // Two sources: (1) bypass query param sets `?email=...` directly, (2) Stripe success_url
  // has session_id which we look up server-side.
  useEffect(() => {
    const directEmail = searchParams.get('email');
    if (directEmail) {
      setStoredScamSmartEmail(directEmail);
      return;
    }
    const sid = searchParams.get('session_id');
    if (!sid) return;
    let cancelled = false;
    const tryLookup = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/scamsmart/session-email`, { params: { session_id: sid } });
        if (!cancelled && data?.email) setStoredScamSmartEmail(data.email);
      } catch (err) {
        if (!cancelled && attempt < 4) {
          setTimeout(() => tryLookup(attempt + 1), 1500); // webhook may not have run yet
        }
      }
    };
    tryLookup();
    return () => { cancelled = true; };
  }, [searchParams]);

  return (
    <div className="ss-page" data-testid="ss-thanks-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">ScamSmart</div>
            </div>
          </Link>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container">
          <div className="ss-preview-card" style={{ textAlign: 'center' }}>
            <ShieldCheck size={64} color="#16B1C4" strokeWidth={1.5} style={{ margin: '0 auto 18px' }} />
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 0 10px', letterSpacing: '-0.3px' }}>
              Welcome to ScamSmart
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, margin: 0 }}>
              Your full access is now active. We've also emailed your welcome details.
            </p>
            <div className="ss-preview-divider" />
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
              Amount paid
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#F9B03E' }}>
              ${amount} AUD
            </div>
          </div>

          <h2 className="ss-paywall-headline">Where to start</h2>
          <p className="ss-paywall-sub">
            Begin with the full 10-scenario Scam IQ Test — it builds your baseline, finds your real weak spot,
            and unlocks the rest of your training.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Link to="/scamsmart/dashboard" className="ss-btn-primary" style={{ justifyContent: 'center', textDecoration: 'none' }} data-testid="ss-thanks-dashboard-btn">
              Open my ScamSmart dashboard <ArrowRight size={18} />
            </Link>
            <div className="ss-trusted" style={{ marginTop: 4 }}>
              <Mail size={16} style={{ verticalAlign: '-3px', marginRight: 8, color: '#16B1C4' }} />
              We've also emailed your welcome and dashboard link. Check your inbox (and spam folder) for an email from
              <strong> All Secured</strong>.
            </div>
          </div>

          <p className="ss-disclaimer">
            Secure payment via Stripe &middot; ScamSmart is awareness and decision-training only — it cannot detect every scam or guarantee protection.
          </p>
        </div>
      </div>

      {confetti && (
        <div aria-hidden="true" style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 9,
        }}>
          {[...Array(36)].map((_, i) => (
            <span key={i} style={{
              position: 'absolute',
              top: '-10px',
              left: `${Math.random() * 100}%`,
              width: 8, height: 8,
              background: ['#16B1C4', '#F9B03E', '#0A1F44', '#1d8a4f'][i % 4],
              borderRadius: i % 2 ? 0 : '999px',
              animation: `ss-confetti ${2 + Math.random() * 1.5}s linear ${Math.random()}s forwards`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }} />
          ))}
          <style>{`
            @keyframes ss-confetti {
              to { transform: translateY(110vh) rotate(720deg); opacity: 0.3; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default ScamSmartThankYou;
